/**
 * Manages timed locks for actions to prevent repeated or conflicting operations within a specific duration.
 * ActionLockManager uses a singleton pattern to ensure that only one instance manages all active locks.
 * Each lock is identified by a unique action ID and is automatically released after a specified duration.
 * Additionally, locks can be manually released if needed before the expiration time.
 */
class ActionLockManager {
    private static instance: ActionLockManager;
    private readonly activeLocks: Map<string, NodeJS.Timeout>;

    private constructor() {
        this.activeLocks = new Map();
    }

    /**
     * Returns the singleton instance of ActionLockManager.
     */
    public static getInstance(): ActionLockManager {
        if (!ActionLockManager.instance) {
            ActionLockManager.instance = new ActionLockManager();
        }
        return ActionLockManager.instance;
    }

    /**
     * Attempts to lock an action by its ID. Returns `true` if the action was not already locked
     * (and is now locked), or `false` if it was already locked.
     * @param actionId - Unique identifier for the action to lock
     * @param lockDuration - Duration in milliseconds for which the action should remain locked (default is 3000 ms)
     * @returns `true` if the action was successfully locked, `false` if it was already locked
     */
    public lock(actionId: string, lockDuration: number = 3000): boolean {
        if (this.activeLocks.has(actionId)) {
            // Action is already locked
            return false;
        }

        // Lock the action ID and set a timeout to release the lock after the specified duration
        const timeout = setTimeout(() => {
            this.activeLocks.delete(actionId);
        }, lockDuration);

        this.activeLocks.set(actionId, timeout);
        return true;
    }

    /**
     * Manually unlocks an action by its ID if needed before the lock duration expires.
     * @param actionId - Unique identifier for the action to unlock
     */
    public unlock(actionId: string): void {
        if (this.activeLocks.has(actionId)) {
            clearTimeout(this.activeLocks.get(actionId));
            this.activeLocks.delete(actionId);
        }
    }

    /**
     * Checks if an action is currently locked.
     * @param actionId - Unique identifier for the action to check
     * @returns `true` if the action is locked, `false` otherwise
     */
    public isLocked(actionId: string): boolean {
        return this.activeLocks.has(actionId);
    }
}

export default ActionLockManager;
